import type { MatchVerdict } from "@synapse/engine";

import type { AriadneReport, GameReport } from "../utils/gameReportUtils";
import type { MatchOutcome } from "./seat";

/** Beating her (spec §5, A Step Ahead): ahead, or she was still out there when the player finished. */
export const beatAriadne = (v: MatchVerdict): boolean =>
  v === "ahead" || v === "left_behind";

export function ariadneReport(o: MatchOutcome): AriadneReport {
  return {
    verdict: o.verdict,
    status: o.status,
    moves: o.her.moves,
    turns: o.her.turns,
    backtracks: o.her.backtracks,
    games: o.games,
    hash: o.hash,
    ...(o.followed ? { followed: true as const } : {}),
  };
}

/** Her moves, turns and backtracks beside the player's (spec §5); null for a report rebuilt from history. */
export function herStatsLine(r: Pick<GameReport, "ariadne">): string | null {
  const a = r.ariadne;
  if (!a || a.turns === 0) return null;
  const n = (k: number, w: string) => `${k} ${w}${k === 1 ? "" : "s"}`;
  return `Ariadne: ${n(a.moves, "move")} · ${n(a.turns, "turn")} · ${n(a.backtracks, "backtrack")}`;
}

/** The share text's arguments for a daily (spec §5): her verdict and moves, never a -1 from an empty thread. */
export function dailyShareArgs(
  r: Pick<GameReport, "aiPath" | "optimalPath" | "ariadne">,
): { verdict: MatchVerdict | null; aiSteps: number; herFinished: boolean } {
  const par = Math.max(0, (r.optimalPath?.length ?? 1) - 1);
  if (!r.ariadne) return { verdict: null, aiSteps: par, herFinished: false };
  return {
    verdict: r.ariadne.verdict,
    aiSteps: r.ariadne.moves,
    herFinished: r.ariadne.status === "waiting" || r.ariadne.status === "won",
  };
}

/** The match verdict line, stated for the player (spec §3.2). */
export function verdictLine(
  r: Pick<GameReport, "totalMoves" | "ariadne">,
): string | null {
  const a = r.ariadne;
  if (!a || a.followed) return null;
  switch (a.verdict) {
    case "ahead":
    case "tied":
      return `You ${r.totalMoves} · Ariadne ${a.moves}`;
    case "behind":
      return `Ariadne ${a.moves} · You ${r.totalMoves}`;
    case "left_behind":
      return "Ariadne was still out there";
    case "gave_up":
      return a.status === "waiting"
        ? `Ariadne got there in ${a.moves}`
        : "Ariadne was still out there";
  }
}
